import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { buildBreadcrumb, SITE } from "@/lib/seo";
import { Check, Star } from "lucide-react";

const TIERS = [
  {
    id: "starter", name: "Starter Investor", price: 97,
    desc: "Solo investors getting started in the marketplace.",
    features: ["Access to local leads", "Submit cash + creative offers", "Basic dashboard", "In-app messaging"],
    cta: "Start with Starter", testid: "tier-starter",
  },
  {
    id: "pro", name: "Pro Investor", price: 297, highlight: true,
    desc: "Active investors who want more deals and tools.",
    features: ["Unlimited offer submissions", "Advanced filtering & alerts", "Saved leads & pipeline", "Walkthrough scheduling", "Verified badge"],
    cta: "Go Pro", testid: "tier-pro",
  },
  {
    id: "city", name: "City Partner", price: 997,
    desc: "Be the featured investor in your market.",
    features: ["City exclusivity options", "Featured placement", "Priority lead routing", "Custom branding"],
    cta: "Claim Your City", testid: "tier-city",
  },
];

export default function Pricing() {
  const offerCatalog = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "ADN Marketplace Provider Plans",
    description: "Monthly subscription plans for investors and providers in the ADN marketplace.",
    brand: { "@type": "Brand", name: SITE.shortName },
    offers: TIERS.map((t) => ({
      "@type": "Offer",
      name: t.name,
      price: String(t.price),
      priceCurrency: "USD",
      url: `${SITE.url}/pricing#${t.id}`,
      availability: "https://schema.org/InStock",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: String(t.price),
        priceCurrency: "USD",
        unitText: "MONTH",
      },
    })),
  };
  return (
    <div>
      <Seo
        title="Pricing — Provider Plans from $97/month"
        description="Sellers always submit free. Providers choose Starter ($97), Pro ($297), City Partner ($997), or Enterprise. No contracts. Cancel anytime."
        path="/pricing"
        jsonLd={[
          offerCatalog,
          buildBreadcrumb([
            { name: "Home", url: "https://adn-marketplace.com/" },
            { name: "Pricing", url: "https://adn-marketplace.com/pricing" },
          ]),
        ]}
      />
      <Navbar />
      <section className="bg-[var(--adn-navy)] text-white grain">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 text-center">
          <p className="eyebrow text-[var(--adn-gold)]">Provider pricing</p>
          <h1 className="font-display text-5xl sm:text-6xl font-light mt-3">Pricing for serious operators.</h1>
          <p className="mt-6 text-white/75 max-w-2xl mx-auto text-lg leading-relaxed">
            Sellers always submit free. Providers choose the plan that fits — from solo investor to city partner.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 -mt-12 pb-24">
        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((t) => (
            <div
              key={t.id}
              className={`bg-white border ${t.highlight ? "border-[var(--adn-gold)] shadow-lg" : "border-[var(--adn-line)]"} p-8 relative`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-8 px-3 py-1 text-[10px] tracking-[0.18em] uppercase font-bold bg-[var(--adn-gold)] text-[var(--adn-navy)] flex items-center gap-1">
                  <Star size={11} /> Most Popular
                </div>
              )}
              <h3 className="font-display text-2xl font-semibold">{t.name}</h3>
              <p className="text-[var(--adn-text-soft)] mt-1 text-sm">{t.desc}</p>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-display text-5xl font-light tabular">${t.price}</span>
                <span className="text-[var(--adn-text-soft)]">/month</span>
              </div>
              <ul className="mt-6 space-y-3">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[15px]">
                    <Check size={16} className="text-[var(--adn-emerald)] mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/login"
                data-testid={t.testid}
                className={`mt-8 block text-center px-6 py-3 rounded text-sm ${t.highlight ? "btn-gold" : "btn-outline-navy"}`}
              >
                {t.cta}
              </Link>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white border border-[var(--adn-line)] p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="eyebrow">Enterprise</p>
            <h3 className="font-display text-2xl font-semibold mt-1">Custom plans for funds & multi-market operators.</h3>
          </div>
          <Link to="/login" className="btn-outline-navy px-6 py-3 rounded text-sm">Contact Sales</Link>
        </div>

        <p className="text-xs text-[var(--adn-text-muted)] text-center mt-10 max-w-3xl mx-auto">
          MVP note: subscription billing is in preview. ADN team will activate billing for approved providers individually.
        </p>
      </section>
      <Footer />
    </div>
  );
}
